import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { selectAuthUser } from '../store/slices/auth/auth.selector';

export type CrmTargetType = 'CONTACT' | 'COMPANY' | 'DEAL';

export const useCrmNavigation = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectAuthUser);
  const orgSlug = user?.orgSlug ?? 'org';

  const base = (contactKey: string) => `/${orgSlug}/crm/contacts/${contactKey}`;
  const companyBase = (companyKey: string) => `/${orgSlug}/crm/companies/${companyKey}`;
  const dealBase = (dealKey: string) => `/${orgSlug}/crm/deals/${dealKey}`;
  const taskBase = (taskKey: string) => `/${orgSlug}/crm/tasks/${taskKey}`;

  const orgBase = () => `/${orgSlug}/crm`;

  // Resolve a task's parent entity detail-page base path from its targetType.
  const targetBase = (targetType: CrmTargetType, targetKey: string) => {
    switch (targetType) {
      case 'CONTACT':
        return base(targetKey);
      case 'COMPANY':
        return companyBase(targetKey);
      case 'DEAL':
        return dealBase(targetKey);
      default:
        return orgBase();
    }
  };

  // Build the URL for a task, landing on its parent entity's Tasks tab with
  // the task highlighted (?tab=tasks&taskUuid=...). TaskList reads taskUuid
  // to scroll to and highlight the matching row.
  const buildViewTaskOnTargetUrl = (
    targetType: CrmTargetType,
    targetKey: string,
    taskUuid: string,
  ) => `${targetBase(targetType, targetKey)}?tab=tasks&taskUuid=${taskUuid}`;

  const toViewTaskOnTarget = (
    targetType: CrmTargetType,
    targetKey: string,
    taskUuid: string,
    replace = false,
  ) => navigate(buildViewTaskOnTargetUrl(targetType, targetKey, taskUuid), { replace });

  return {
    orgSlug,

    /** /{orgSlug}/crm/contacts */
    toContacts: (replace = false) =>
      navigate(`/${orgSlug}/crm/contacts`, { replace }),

    /** /{orgSlug}/crm/create */
    toCreateContact: (replace = false) =>
      navigate(`/${orgSlug}/crm/contact/create`, { replace }),

    /** /{orgSlug}/crm/{contactUuid}/edit */
    toEditContact: (contactUuid: string, replace = false) =>
      navigate(`${base(contactUuid)}/edit`, { replace }),

    /** /{orgSlug}/crm/{contactUuid} */
    toViewContact: (contactKey: string, replace = false) =>
      navigate(base(contactKey), { replace }),
    /** /{orgSlug}/crm/companies/{companyKey} */
    toViewCompany: (companyKey: string, replace = false) =>
      navigate(`${orgBase()}/companies/${companyKey}`, { replace }),

    /** /{orgSlug}/crm/companies */
    toCompanies: (replace = false) =>
      navigate(`${orgBase()}/companies`, { replace }),

    /** /{orgSlug}/crm/companies/create */
    toCreateCompany: (replace = false) =>
      navigate(`${orgBase()}/companies/create`, { replace }),

    /** /{orgSlug}/crm/deals */
    toDeals: (replace = false) =>
      navigate(`/${orgSlug}/crm/deals`, { replace }),

    /** /{orgSlug}/crm/deals/{dealKey} */
    toViewDeal: (dealKey: string, replace = false) =>
      navigate(dealBase(dealKey), { replace }),

    /** /{orgSlug}/crm/deals/create */
    toCreateDeal: (replace = false) =>
      navigate(`/${orgSlug}/crm/deals/create`, { replace }),

    /** /{orgSlug}/crm/tasks/{taskKey} */
    toViewTask: (taskKey: string, replace = false) =>
      navigate(taskBase(taskKey), { replace }),

    /**
     * Route a task to its parent entity's detail page, Tasks tab, with the
     * task highlighted. Use this from any global task list (e.g. TaskTable).
     */
    toViewTaskOnTarget,

    toTasks: (replace = false) =>
      navigate(`/${orgSlug}/crm/tasks`, { replace }),

    /** Build a raw URL string (for breadcrumbs / Link to= props) */
    buildContactsUrl: () => `/${orgSlug}/crm/contacts`,
    buildDealsUrl: () => `/${orgSlug}/crm/deals`,
    buildViewDealUrl: (dealKey: string) => dealBase(dealKey),
    buildCreateDealUrl: () => `/${orgSlug}/crm/deals/create`,
    buildCompaniesUrl: () => `/${orgSlug}/crm/companies`,
    buildCreateCompanyUrl: () => `/${orgSlug}/crm/companies/create`,
    buildCreateContactUrl: () => `/${orgSlug}/crm/contacts/create`,
    buildEditContactUrl: (contactUuid: string) => `${base(contactUuid)}/edit`,
    buildViewContactUrl: (contactUuid: string) => base(contactUuid),
    buildContactUrl: (contactUuid: string) => base(contactUuid),
    buildTasksUrl: () => `/${orgSlug}/crm/tasks`,
    buildCreateTaskUrl: () => `/${orgSlug}/crm/tasks/create`,
    buildViewTaskOnTargetUrl,
  };
};